import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    DatePicker,
    Button,
    Space,
    Table,
    Tag,
    Divider,
    message,
    Typography,
    Spin
} from 'antd';
import {
    DollarOutlined,
    UserOutlined,
    CalendarOutlined,
    MedicineBoxOutlined,
    TeamOutlined,
    FileTextOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useApp } from '../../context/AppContext';
import api from '../../services/api';
import { formatCurrency } from '../../utils/currency';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const Reports = () => {
    const { t } = useTranslation(['reports', 'common']);
    const { user } = useApp();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);

    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        fetchReport();
    }, [dateRange]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const endpoint = isAdmin ? '/reports/clinic' : '/reports/dentist';
            const params = dateRange[0] && dateRange[1] ? {
                startDate: dateRange[0].toISOString(),
                endDate: dateRange[1].toISOString()
            } : {};

            const response = await api.get(endpoint, { params });

            if (response.data.success) {
                setReportData(response.data.data);
            }
        } catch (error) {
            message.error(t('reports:messages.fetchFailed'));
            console.error('Error fetching report:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (dates) => {
        setDateRange(dates || [null, null]);
    };

    const handleRefresh = () => {
        fetchReport();
    };

    if (loading && !reportData) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    // Dentist performance columns (admin only)
    const dentistPerformanceColumns = [
        {
            title: t('reports:dentistName'),
            dataIndex: 'dentistName',
            key: 'dentistName'
        },
        {
            title: t('reports:totalTreatments'),
            dataIndex: 'treatmentCount',
            key: 'treatmentCount',
            sorter: (a, b) => a.treatmentCount - b.treatmentCount
        },
        {
            title: t('reports:totalRevenue'),
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            render: (value) => `$${value?.toFixed(2) || 0}`,
            sorter: (a, b) => a.totalRevenue - b.totalRevenue
        }
    ];

    // Treatment type columns
    const treatmentColumns = [
        {
            title: t('reports:treatmentType'),
            dataIndex: '_id',
            key: 'type'
        },
        {
            title: t('reports:count'),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => a.count - b.count
        },
        {
            title: t('reports:revenue'),
            dataIndex: 'totalCost',
            key: 'revenue',
            render: (value) => `$${(value || 0).toFixed(2)}`,
            sorter: (a, b) => (a.totalCost || 0) - (b.totalCost || 0)
        }
    ];

    // Helper to get revenue data by currency
    const getRevenueByCurrency = () => {
        const data = isAdmin ? reportData?.overview?.revenueByCurrency : reportData?.summary?.revenueByCurrency;
        return data || {};
    };

    const currencies = Object.keys(getRevenueByCurrency());

    const renderCurrencySymbol = (currency) => currency === 'IQD' ? 'IQD' : '$';

    const renderStatisticCard = (title, value, icon, prefix = '', suffix = '', valueStyle = {}) => (
        <Card bordered={false}>
            <Statistic
                title={title}
                value={value}
                prefix={prefix || icon}
                suffix={suffix}
                valueStyle={valueStyle}
            />
        </Card>
    );

    return (
        <div>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={2}>
                        {isAdmin ? t('reports:clinicReport') : t('reports:myReport')}
                    </Title>
                </Col>
                <Col>
                    <Space>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateChange}
                            format="YYYY-MM-DD"
                        />
                        <Button type="primary" onClick={handleRefresh} loading={loading}>
                            {t('common:refresh')}
                        </Button>
                    </Space>
                </Col>
            </Row>

            {reportData && (
                <>
                    {/* Overview Statistics */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        {isAdmin ? (
                            <>
                                <Col xs={24} sm={12} md={6}>
                                    {renderStatisticCard(
                                        t('reports:totalPatients'),
                                        reportData.overview?.totalPatients || 0,
                                        <UserOutlined style={{ color: '#1890ff' }} />
                                    )}
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    {renderStatisticCard(
                                        t('reports:newPatients'),
                                        reportData.overview?.newPatients || 0,
                                        <UserOutlined style={{ color: '#52c41a' }} />
                                    )}
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    {renderStatisticCard(
                                        t('reports:totalDentists'),
                                        reportData.overview?.totalDentists || 0,
                                        <TeamOutlined style={{ color: '#722ed1' }} />
                                    )}
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    {renderStatisticCard(
                                        t('reports:totalAppointments'),
                                        reportData.overview?.totalAppointments || 0,
                                        <CalendarOutlined style={{ color: '#fa8c16' }} />
                                    )}
                                </Col>
                            </>
                        ) : (
                            <>
                                <Col xs={24} sm={12} md={8}>
                                    {renderStatisticCard(
                                        t('reports:totalPatients'),
                                        reportData.summary?.totalPatients || 0,
                                        <UserOutlined style={{ color: '#1890ff' }} />
                                    )}
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    {renderStatisticCard(
                                        t('reports:totalAppointments'),
                                        reportData.summary?.totalAppointments || 0,
                                        <CalendarOutlined style={{ color: '#fa8c16' }} />
                                    )}
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    {renderStatisticCard(
                                        t('reports:totalTreatments'),
                                        reportData.summary?.totalTreatments || 0,
                                        <MedicineBoxOutlined style={{ color: '#52c41a' }} />
                                    )}
                                </Col>
                            </>
                        )}
                    </Row>

                    {/* Revenue Statistics - Separated by Currency */}
                    {currencies.length > 0 ? currencies.map(currency => {
                        const currencyData = getRevenueByCurrency()[currency] || {};
                        const symbol = renderCurrencySymbol(currency);
                        return (
                            <div key={currency}>
                                <Title level={4} style={{ marginBottom: 12 }}>
                                    {t('reports:revenueIn', { currency, defaultValue: 'Revenue ({{currency}})' })}
                                </Title>
                                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Card bordered={false}>
                                            <Statistic
                                                title={`${t('reports:totalRevenue')} (${currency})`}
                                                value={currencyData.totalRevenue || 0}
                                                precision={currency === 'IQD' ? 0 : 2}
                                                prefix={<span style={{ color: '#52c41a' }}>{symbol} </span>}
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Card bordered={false}>
                                            <Statistic
                                                title={`${t('reports:totalCollected', 'Collected')} (${currency})`}
                                                value={currencyData.totalCollected || 0}
                                                precision={currency === 'IQD' ? 0 : 2}
                                                prefix={<span style={{ color: '#1890ff' }}>{symbol} </span>}
                                                valueStyle={{ color: '#1890ff' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Card bordered={false}>
                                            <Statistic
                                                title={`${t('reports:totalPending', 'Pending')} (${currency})`}
                                                value={currencyData.totalPending || 0}
                                                precision={currency === 'IQD' ? 0 : 2}
                                                prefix={<span style={{ color: '#ff4d4f' }}>{symbol} </span>}
                                                valueStyle={{ color: '#ff4d4f' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        );
                    }) : (
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} md={8}>
                                <Card bordered={false}>
                                    <Statistic
                                            title={t('reports:totalRevenue')}
                                            value={0}
                                        precision={2}
                                            prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                                            valueStyle={{ color: '#52c41a' }}
                                    />
                                </Card>
                                </Col>
                            </Row>
                    )}

                    <Divider />

                    {/* Appointment Statistics */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={24} lg={12}>
                            <Card title={t('reports:appointmentsByStatus')} bordered={false}>
                                {(reportData.appointments?.byStatus || []).map((item) => (
                                    <div key={item._id} style={{ marginBottom: 12 }}>
                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <Tag color={
                                                    item._id === 'Completed' ? 'green' :
                                                        item._id === 'Cancelled' ? 'red' :
                                                            item._id === 'Scheduled' ? 'blue' : 'orange'
                                                }>
                                                    {item._id}
                                                </Tag>
                                            </Col>
                                            <Col>
                                                <Text strong>{item.count}</Text>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </Card>
                        </Col>

                        {/* Treatment Types */}
                        <Col xs={24} lg={12}>
                            <Card
                                title={t('reports:treatmentsByType')}
                                bordered={false}
                                extra={
                                    <Text type="secondary">
                                        {t('reports:total')}: {reportData.treatments?.total || 0}
                                    </Text>
                                }
                            >
                                <Table
                                    dataSource={reportData.treatments?.byType || []}
                                    columns={treatmentColumns}
                                    pagination={{ pageSize: 5 }}
                                    rowKey="_id"
                                    size="small"
                                    scroll={{ x: 'max-content' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Payment Status - shown for both admin and dentist */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={24}>
                            <Card title={t('reports:paymentsByStatus', 'Payments by Status')} bordered={false}>
                                {(reportData.payments?.byStatus || []).map((item, index) => {
                                    const statusKey = item._id?.status || item._id;
                                    const currency = item._id?.currency || 'USD';
                                    const symbol = renderCurrencySymbol(currency);
                                    return (
                                        <div key={index} style={{ marginBottom: 12 }}>
                                            <Row justify="space-between" align="middle">
                                                <Col>
                                                    <Space>
                                                        <Tag color={
                                                            statusKey === 'Paid' ? 'green' :
                                                                statusKey === 'Pending' ? 'orange' :
                                                                    statusKey === 'Partial' ? 'blue' : 'default'
                                                        }>
                                                            {statusKey}
                                                        </Tag>
                                                        <Tag>{currency}</Tag>
                                                    </Space>
                                                </Col>
                                                <Col>
                                                    <Space direction="vertical" size="small" style={{ textAlign: 'right' }}>
                                                        <Text type="secondary">{item.count} {t('reports:invoices', 'invoices')}</Text>
                                                        <Text strong>{symbol}{currency === 'IQD' ? (item.totalAmount || 0).toLocaleString() : (item.totalAmount || 0).toFixed(2)}</Text>
                                                    </Space>
                                                </Col>
                                            </Row>
                                        </div>
                                    );
                                })}
                                {(!reportData.payments?.byStatus || reportData.payments.byStatus.length === 0) && (
                                    <Text type="secondary">{t('reports:noData', 'No payment data available')}</Text>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {/* Admin-only sections */}
                    {isAdmin && (
                        <>
                            <Divider />

                            {/* Dentist Performance */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24}>
                                    <Card title={t('reports:dentistPerformance')} bordered={false}>
                                        <Table
                                            dataSource={reportData.dentistPerformance || []}
                                            columns={dentistPerformanceColumns}
                                            pagination={{ pageSize: 10 }}
                                            rowKey="_id"
                                            scroll={{ x: 'max-content' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Payment Methods */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24}>
                                    <Card title={t('reports:paymentMethods')} bordered={false}>
                                        {(reportData.payments?.byMethod || []).map((item, index) => {
                                            const method = item._id?.method || item._id || 'Unknown';
                                            const currency = item._id?.currency || 'USD';
                                            const symbol = renderCurrencySymbol(currency);
                                            return (
                                                <div key={index} style={{ marginBottom: 12 }}>
                                                    <Row justify="space-between" align="middle">
                                                        <Col>
                                                            <Space>
                                                                <Text>{method}</Text>
                                                                <Tag>{currency}</Tag>
                                                            </Space>
                                                        </Col>
                                                        <Col>
                                                            <Space>
                                                                <Text type="secondary">{item.count} {t('reports:transactions')}</Text>
                                                                <Text strong>{symbol}{currency === 'IQD' ? (item.total || 0).toLocaleString() : (item.total || 0).toFixed(2)}</Text>
                                                            </Space>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            );
                                        })}
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Expenses Section */}
                    <Divider />
                    <Title level={3} style={{ marginBottom: 16 }}>
                        {t('reports:expenses', 'Expenses')}
                    </Title>

                    {/* Expense Totals by Currency */}
                    {(() => {
                        const expCurrencies = Object.keys(reportData.expenses?.expensesByCurrency || {});
                        if (expCurrencies.length > 0) {
                            return (
                                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                    {expCurrencies.map(currency => {
                                        const expData = reportData.expenses.expensesByCurrency[currency];
                                        const symbol = renderCurrencySymbol(currency);
                                        return (
                                            <Col xs={24} sm={12} md={8} key={currency}>
                                                <Card bordered={false}>
                                                    <Statistic
                                                        title={`${t('reports:totalExpenses', 'Total Expenses')} (${currency})`}
                                                        value={expData.total || 0}
                                                        precision={currency === 'IQD' ? 0 : 2}
                                                        prefix={<span style={{ color: '#ff4d4f' }}>{symbol} </span>}
                                                        valueStyle={{ color: '#ff4d4f' }}
                                                    />
                                                    <Text type="secondary">{expData.count || 0} {t('reports:entries', 'entries')}</Text>
                                                </Card>
                                            </Col>
                                        );
                                    })}

                                    {/* Net Profit/Loss Cards */}
                                    {(() => {
                                        const allCurrencies = [...new Set([
                                            ...Object.keys(getRevenueByCurrency()),
                                            ...expCurrencies
                                        ])];
                                        return allCurrencies.map(currency => {
                                            const revenue = getRevenueByCurrency()[currency]?.totalCollected || 0;
                                            const expense = reportData.expenses?.expensesByCurrency?.[currency]?.total || 0;
                                            const net = revenue - expense;
                                            const symbol = renderCurrencySymbol(currency);
                                            return (
                                                <Col xs={24} sm={12} md={8} key={`net-${currency}`}>
                                                    <Card bordered={false}>
                                                        <Statistic
                                                            title={`${t('reports:netProfit', 'Net Profit')} (${currency})`}
                                                            value={Math.abs(net)}
                                                            precision={currency === 'IQD' ? 0 : 2}
                                                            prefix={<span style={{ color: net >= 0 ? '#52c41a' : '#ff4d4f' }}>{net < 0 ? '-' : ''}{symbol} </span>}
                                                            valueStyle={{ color: net >= 0 ? '#52c41a' : '#ff4d4f' }}
                                                        />
                                                    </Card>
                                                </Col>
                                            );
                                        });
                                    })()}
                                </Row>
                            );
                        }
                        return (
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} md={8}>
                                    <Card bordered={false}>
                                        <Statistic
                                            title={t('reports:totalExpenses', 'Total Expenses')}
                                            value={0}
                                            precision={2}
                                            prefix={<DollarOutlined style={{ color: '#ff4d4f' }} />}
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        );
                    })()}

                    {/* Expenses by Category */}
                    {(reportData.expenses?.byCategory || []).length > 0 && (
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24}>
                                <Card title={t('reports:expensesByCategory', 'Expenses by Category')} bordered={false}>
                                    {(reportData.expenses?.byCategory || []).map((item, index) => {
                                        const category = item._id?.category || item._id || 'Other';
                                        const currency = item._id?.currency || 'USD';
                                        const symbol = renderCurrencySymbol(currency);
                                        return (
                                            <div key={index} style={{ marginBottom: 12 }}>
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <Space>
                                                            <Tag color="red">{category}</Tag>
                                                            <Tag>{currency}</Tag>
                                                        </Space>
                                                    </Col>
                                                    <Col>
                                                        <Space>
                                                            <Text type="secondary">{item.count} {t('reports:entries', 'entries')}</Text>
                                                            <Text strong style={{ color: '#ff4d4f' }}>
                                                                {symbol}{currency === 'IQD' ? (item.total || 0).toLocaleString() : (item.total || 0).toFixed(2)}
                                                            </Text>
                                                        </Space>
                                                    </Col>
                                                </Row>
                                            </div>
                                        );
                                    })}
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </div>
    );
};

export default Reports;
